import { defineMessages, type MessageDescriptor } from 'react-intl';

import {
  ACHIEVEMENT_CATEGORIES,
  ACHIEVEMENT_FALLBACK_TYPES,
  ACHIEVEMENT_TIERS,
  ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';

export const achievementsCategoriesMessages = defineMessages<ACHIEVEMENT_CATEGORIES>({
  [ACHIEVEMENT_CATEGORIES.DISCOVERY]: {
    defaultMessage: 'Découverte',
  },

  [ACHIEVEMENT_CATEGORIES.ATTENDANCE]: {
    defaultMessage: "Badges d'assiduité",
  },

  [ACHIEVEMENT_CATEGORIES.SOCIAL]: {
    defaultMessage: 'Communauté',
  },
});

export const achievementMessages: Partial<
  Record<
    ACHIEVEMENT_TYPES | ACHIEVEMENT_FALLBACK_TYPES,
    Partial<
      Record<
        ACHIEVEMENT_TIERS,
        {
          TITLE: MessageDescriptor;
          DESCRIPTION: MessageDescriptor;
          CATCH_PHRASE?: MessageDescriptor;
        }
      >
    >
  >
> = {
  [ACHIEVEMENT_TYPES.CONNECTION_STREAK]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: 'Élève assidu' },
      DESCRIPTION: {
        defaultMessage: "Aller sur le campus 5 jours d'affilée",
      },
      CATCH_PHRASE: {
        defaultMessage: "5 jours de présence d'affilée, pas mal !",
      },
    }),
    [ACHIEVEMENT_TIERS.BRONZE]: defineMessages({
      TITLE: { defaultMessage: 'Élève engagé' },
      DESCRIPTION: {
        defaultMessage: "Aller sur le campus 15 jours d'affilée",
      },
      CATCH_PHRASE: {
        defaultMessage: "15 jours de présence d'affilée, bravo !",
      },
    }),
    [ACHIEVEMENT_TIERS.SILVER]: defineMessages({
      TITLE: { defaultMessage: 'Pilier du campus' },
      DESCRIPTION: {
        defaultMessage: "Aller sur le campus 30 jours d'affilée",
      },
      CATCH_PHRASE: {
        defaultMessage: "30 jours de présence d'affilée, wow !",
      },
    }),
    [ACHIEVEMENT_TIERS.GOLD]: defineMessages({
      TITLE: { defaultMessage: 'Légende locale' },
      DESCRIPTION: {
        defaultMessage: "Aller sur le campus 100 jours d'affilée",
      },
      CATCH_PHRASE: {
        defaultMessage: "100 jours de présence d'affilée, incroyable !",
      },
    }),
  },
  [ACHIEVEMENT_TYPES.EASTER_EGGS_2025]: {
    [ACHIEVEMENT_TIERS.GOLD]: defineMessages({
      TITLE: { defaultMessage: 'Indiana' },
      DESCRIPTION: {
        defaultMessage: 'Trouvez les 10 œufs cachés',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Qui vole un œuf...',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.EXERCISE_COMPLETED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: 'Cerveau en action' },
      DESCRIPTION: { defaultMessage: 'Finir un exercice' },
      CATCH_PHRASE: {
        defaultMessage: 'Un exercice terminé. Une victoire de plus !',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE]: {
    [ACHIEVEMENT_TIERS.GOLD]: defineMessages({
      TITLE: { defaultMessage: 'Sans faute !' },
      DESCRIPTION: { defaultMessage: 'Obtenir 100% à un exercice' },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez obtenu un score parfait à un exercice',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: {
        defaultMessage: 'Vous avez quelques minutes ?',
      },
      DESCRIPTION: {
        defaultMessage: 'Répondre à un sondage',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Merci pour vos retours',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.HELP_IT_TEAM]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: {
        defaultMessage: 'On vous embauche ?',
      },
      DESCRIPTION: {
        defaultMessage: "Aider l'équipe de Didaskill",
      },
      CATCH_PHRASE: {
        defaultMessage: "De la part de toute l'équipe : Merci ! Vous nous avez vraiment aidé !",
      },
    }),
  },

  [ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: 'Beat drop' },
      DESCRIPTION: {
        defaultMessage: 'Réaliser une écoute audio',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Une bonne première écoute',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.LESSON_COMPLETED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: "J'y cours" },
      DESCRIPTION: { defaultMessage: 'Terminer un cours' },
      CATCH_PHRASE: {
        defaultMessage: 'Premier cours terminé ! Ça, c’est fait.',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: 'Allô ?' },
      DESCRIPTION: {
        defaultMessage: 'Prendre rdv avec mon coach ou référent',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Premier échange réussi !',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: { defaultMessage: 'Mon beau miroir' },
      DESCRIPTION: {
        defaultMessage: 'Ajouter ma photo de profil',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous êtes superbe',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: {
        defaultMessage: 'La classe à Dallas',
      },
      DESCRIPTION: {
        defaultMessage: 'Assister à une classe virtuelle',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.CLASS_JOINED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: defineMessages({
      TITLE: {
        defaultMessage: 'La classe du siècle',
      },
      DESCRIPTION: {
        defaultMessage: 'Découvrir les membres de ma classe',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Bienvenue dans votre promo !',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.CAMPUS_EXPLORER]: {
    [ACHIEVEMENT_TIERS.QUEST]: defineMessages({
      TITLE: {
        defaultMessage: 'Explorateur de campus',
      },
      DESCRIPTION: {
        defaultMessage: 'Accomplir la quête associée',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez terminé la quête',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.GOOD_HABITS]: {
    [ACHIEVEMENT_TIERS.QUEST]: defineMessages({
      TITLE: {
        defaultMessage: 'Prendre de bonnes habitudes',
      },
      DESCRIPTION: {
        defaultMessage: 'Accomplir la quête associée',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez terminé la quête',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.GAIN_CONFIDENCE]: {
    [ACHIEVEMENT_TIERS.QUEST]: defineMessages({
      TITLE: {
        defaultMessage: 'Gagner en confiance',
      },
      DESCRIPTION: {
        defaultMessage: 'Accomplir la quête associée',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez terminé la quête',
      },
    }),
  },

  [ACHIEVEMENT_TYPES.SNAP_FINGERS]: {
    [ACHIEVEMENT_TIERS.QUEST]: defineMessages({
      TITLE: {
        defaultMessage: 'En claquant des doigts',
      },
      DESCRIPTION: {
        defaultMessage: 'Accomplir la quête associée',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez terminé la quête',
      },
    }),
  },

  [ACHIEVEMENT_FALLBACK_TYPES.DEFAULT]: {
    [ACHIEVEMENT_TIERS.GOLD]: defineMessages({
      // Unused
      TITLE: {
        defaultMessage: 'DEFAULT_TITLE',
      },
      // Unused
      DESCRIPTION: {
        defaultMessage: 'DEFAULT_DESCRIPTION',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez un nouveau badge !',
      },
    }),
  },

  [ACHIEVEMENT_FALLBACK_TYPES.DEFAULT_MULTIPLE]: {
    [ACHIEVEMENT_TIERS.GOLD]: defineMessages({
      // Unused
      TITLE: {
        defaultMessage: 'MULTIPLE_DEFAULT_TITLE',
      },
      // Unused
      DESCRIPTION: {
        defaultMessage: 'MULTIPLE_DEFAULT_DESCRIPTION',
      },
      CATCH_PHRASE: {
        defaultMessage: 'Vous avez des nouveaux badges !',
      },
    }),
  },
};
